// Type-only: the control touches no mapbox-gl runtime API, so it stays importable (and testable)
// without loading the library.
import type mapboxgl from 'mapbox-gl';
import {
  mapDataCreditsForSources,
  type MapDataCredit,
  type StyleSourceLike,
} from '../models/MapDataCredit';

function prefixKeys(
  sources: Record<string, StyleSourceLike | null | undefined>,
  prefix: string,
): Record<string, StyleSourceLike | null | undefined> {
  return Object.fromEntries(
    Object.entries(sources).map(([key, value]) => [`${prefix}${key}`, value]),
  );
}

/**
 * Paints the data-provider credits a loaded style owes, for the maps that run without GL JS's own
 * `AttributionControl`. See `MapDataCredit` for why those maps need it and the ones with the control
 * don't.
 *
 * Added like any other control — `map.addControl(new MapDataCreditControl(), 'bottom-right')` — so it
 * lands in the corner the attribution strip would have occupied, and re-reads the style on every
 * `styledata` event, which covers a full `setStyle` and a diffed one alike.
 *
 * Styling is inline rather than in a stylesheet because the element is created outside any
 * component's template, so the `scoped` CSS of the four components that mount it would not apply.
 */
export class MapDataCreditControl implements mapboxgl.IControl {
  private map: mapboxgl.Map | null = null;
  private container: HTMLElement | null = null;
  private rendered: string | null = null;
  private additionalSources: Record<string, StyleSourceLike | null | undefined> = {};

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.map = map;

    const container = document.createElement('div');
    container.className = 'mapboxgl-ctrl hiiker-map-data-credit';
    container.style.background = 'rgba(0, 0, 0, 0.35)';
    container.style.borderRadius = '4px';
    container.style.padding = '2px 6px';
    container.style.font = '400 11px/1.4 Montserrat, sans-serif';
    this.container = container;

    map.on('styledata', this.refresh);
    this.refresh();

    return container;
  }

  onRemove(): void {
    this.map?.off('styledata', this.refresh);
    this.container?.parentNode?.removeChild(this.container);
    this.container = null;
    this.map = null;
  }

  /**
   * Credits a second map's style through this control as well.
   *
   * For the compare-maps modal, where two full-screen maps are crossfaded: a control on the faded
   * overlay would fade with it, and a second pill would land under the opacity bar. So one control
   * on the un-faded base map carries both styles' credits.
   */
  setAdditionalSources(
    sources: Record<string, StyleSourceLike | null | undefined> | null | undefined,
  ): void {
    this.additionalSources = sources ?? {};
    this.refresh();
  }

  /**
   * Re-reads the loaded style. `getStyle()` throws while the style is still resolving, which
   * `styledata` can fire during, so a failed read just leaves the last render in place — the next
   * event repaints it.
   */
  private refresh = (): void => {
    if (!this.container || !this.map) return;

    let credits: MapDataCredit[] = [];
    try {
      credits = mapDataCreditsForSources({
        ...((this.map.getStyle()?.sources ?? {}) as Record<string, StyleSourceLike>),
        // Prefixed so a same-named source in the other style cannot shadow this one.
        ...prefixKeys(this.additionalSources, 'additional:'),
      });
    } catch {
      return;
    }

    const signature = credits.map((credit) => credit.label).join('|');
    if (signature === this.rendered) return;
    this.rendered = signature;

    this.container.replaceChildren(...credits.map(MapDataCreditControl.link));
    // An empty pill would still paint its background over the map.
    this.container.style.display = credits.length ? '' : 'none';
  };

  private static link(credit: MapDataCredit): HTMLAnchorElement {
    const link = document.createElement('a');
    link.href = credit.attributionUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = credit.label;
    link.style.color = '#fff';
    link.style.textDecoration = 'none';
    return link;
  }
}
